import { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, ArrowUpCircle, ArrowDownCircle, KeyRound, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";

const KIND = {
  grant: { label: "Top-up", Icon: ArrowUpCircle, cls: "text-emerald-600 bg-emerald-50" },
  clawback: { label: "Taken back", Icon: ArrowDownCircle, cls: "text-rose-600 bg-rose-50" },
  generate: { label: "Codes generated", Icon: KeyRound, cls: "text-indigo-600 bg-indigo-50" },
  refund: { label: "Refund", Icon: RotateCcw, cls: "text-amber-600 bg-amber-50" },
};

const fmt = (d) => new Date(d).toLocaleString([], { dateStyle: "medium", timeStyle: "short" });

export const CreditLedgerDialog = ({ userId, name, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRows(null);
    api.get(userId ? `/admin/sub-users/${userId}/ledger` : "/admin/ledger")
      .then((r) => setRows(r.data))
      .catch(() => { toast.error("Could not load credit history"); setRows([]); });
  }, [open, userId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {compact ? (
          <Button size="sm" variant="outline" className="rounded-lg h-8" title="Credit history" data-testid={`ledger-${name}`}>
            <History className="w-3.5 h-3.5" />
          </Button>
        ) : (
          <Button variant="outline" className="rounded-xl font-heading font-bold" data-testid="my-ledger-btn">
            <History className="w-4 h-4 mr-1" /> Credit history
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ledger-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading font-extrabold">Credit history{name && userId ? ` · ${name}` : ""}</DialogTitle>
          <DialogDescription className="font-body">Every top-up, clawback, refund and code generation, newest first.</DialogDescription>
        </DialogHeader>
        {rows === null ? (
          <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : rows.length === 0 ? (
          <p className="font-body text-sm text-slate-500 py-8 text-center" data-testid="ledger-empty">No credit activity yet.</p>
        ) : (
          <ol className="relative border-l border-slate-200 ml-3 mt-2 space-y-4" data-testid="ledger-list">
            {rows.map((r) => {
              const k = KIND[r.type] || KIND.grant;
              return (
                <li key={r.entry_id} className="ml-5" data-testid={`ledger-entry-${r.entry_id}`}>
                  <span className={`absolute -left-[13px] w-6 h-6 rounded-full flex items-center justify-center ${k.cls}`}>
                    <k.Icon className="w-3.5 h-3.5" />
                  </span>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-heading font-bold text-sm text-slate-900">{k.label}
                        <span className={`ml-2 font-mono ${r.delta > 0 ? "text-emerald-600" : "text-rose-600"}`}>{r.delta > 0 ? `+${r.delta}` : r.delta}</span>
                      </p>
                      <p className="font-body text-xs text-slate-500">{r.note}{r.code ? ` · ${r.code}` : ""}</p>
                      <p className="font-body text-[11px] text-slate-400 mt-0.5">{fmt(r.created_at)} · by {r.actor_email}</p>
                    </div>
                    <p className="font-body text-xs text-slate-500 whitespace-nowrap">Balance <span className="font-heading font-bold text-slate-800">{r.balance_after}</span></p>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
};
